import type { CurveInfo } from '../visuals/simple-chart.ts'
import {
    getPitchTypeColor,
    getPitchTypeColorGrouping,
} from '../utils/pitch-type-colors'
import type {
    AugmentedBin,
    DistributionBin,
    DistributionObj,
    HandednessWithCombination,
} from './Wrapper'
import { isLeagueKey } from './key-parsing.ts'
import getDistributionColor from './utils/get-distribution-color.ts'
import { Dimension } from './swing-timing-chart.ts'
import type { PitchTypeColorMode } from './utils/can-use-pitch-type-colors.ts'

type PitchTypeFilterMode = 'group03' | 'group09'

// Generally would be two (a L and a R), but make it generic
function combineBins(
    binsArr: DistributionBin[],
    batSide: HandednessWithCombination,
    pitchHand: HandednessWithCombination
): DistributionBin[] {
    if (!binsArr.length) {
        return []
    }

    const total = binsArr.reduce((sum, curr) => sum + curr.n, 0)
    binsArr.sort((a, b) => a.bin - b.bin)

    const newBins = []
    let lastBin = binsArr[0]
    let currentSum = lastBin.n
    let currentRunExpSum = lastBin.avg_run_exp * lastBin.n
    for (let i = 1; i < binsArr.length; i++) {
        const currentBin = binsArr[i]
        if (lastBin.bin === currentBin.bin) {
            currentSum += currentBin.n
            currentRunExpSum += currentBin.avg_run_exp * currentBin.n
        } else {
            newBins.push({
                ...lastBin,
                pitch_hand: pitchHand,
                bat_side: batSide,
                avg_run_exp: currentRunExpSum / currentSum,
                n: currentSum,
                n_pct: currentSum / total,
            })

            currentSum = currentBin.n
            currentRunExpSum = currentBin.avg_run_exp * currentBin.n
            lastBin = currentBin
        }
    }

    newBins.push({
        ...lastBin,
        avg_run_exp: currentRunExpSum / currentSum,
        n: currentSum,
        n_pct: currentSum / total,
    })

    return newBins
}

function filterDimensionArray(
    pitchTypeArr: DistributionBin[],
    pitchHand: HandednessWithCombination | null,
    batSide: HandednessWithCombination | null
) {
    const arr = pitchTypeArr

    const effectivePitchHand = pitchHand ?? 'X'
    const effectiveBatSide = batSide ?? 'X'
    if (effectivePitchHand !== 'X' && effectiveBatSide !== 'X') {
        return arr.filter(
            (d) =>
                d.bat_side === effectiveBatSide &&
                d.pitch_hand === effectivePitchHand
        )
    }

    // Combine it all
    if (effectivePitchHand === 'X' && effectiveBatSide === 'X') {
        return combineBins(arr, effectiveBatSide, effectivePitchHand)
    }

    if (effectivePitchHand === 'X') {
        const pitchTypeBatSide = arr.filter(
            (d) => d.bat_side === effectiveBatSide
        )
        return combineBins(
            pitchTypeBatSide,
            effectiveBatSide,
            effectivePitchHand
        )
    }

    if (effectiveBatSide === 'X') {
        const pitchTypePitchHand = arr.filter(
            (d) => d.pitch_hand === effectivePitchHand
        )
        return combineBins(
            pitchTypePitchHand,
            effectiveBatSide,
            effectivePitchHand
        )
    }

    throw new Error('HOW DID WE GET HERE?')
}

export type ProcessedPitchData = Omit<CurveInfo, 'data'> & {
    data: AugmentedBin[]
}

export default function processPitchData(
    dataObj: DistributionObj,
    dim: Dimension,
    pitchType: string | null,
    pitchHand: HandednessWithCombination | null,
    batSide: HandednessWithCombination | null,
    key: string,
    pitchTypeColorMode: PitchTypeColorMode,
    strokeOnly: boolean = false,
    year: number | null = null,
    pitchTypeFilterMode: PitchTypeFilterMode = pitchTypeColorMode === 'group09'
        ? 'group09'
        : 'group03'
): ProcessedPitchData {
    const data = dataObj[dim]

    // Only respond with pitch type data now...
    const dimensionData = data
    const isLeagueCurve = isLeagueKey(key)
    const pitchTypeField =
        pitchTypeFilterMode === 'group09'
            ? 'api_pitch_type_group09'
            : 'api_pitch_type_group03'

    const filteredByContext = dimensionData
        .filter((d) => !isLeagueCurve || year === null || d.year === year)
        .filter(
            (d) =>
                !isLeagueCurve ||
                pitchType === null ||
                d[pitchTypeField] === pitchType
        )

    const filteredData = filterDimensionArray(
        filteredByContext,
        pitchHand,
        batSide
    )

    const total = filteredData.reduce((acc, curr) => acc + curr.n, 0)

    const filteredDataAugmented = filteredData.map((b) => ({
        ...b,
        n_pct: b.n / total,
        // key: keyFn(b),
    }))

    let color = getDistributionColor(key)

    if (pitchTypeColorMode !== 'none' && pitchType !== null) {
        color =
            pitchTypeColorMode === 'group09'
                ? getPitchTypeColorGrouping(pitchType)
                : getPitchTypeColor(pitchType)
    }

    return {
        color,
        data: filteredDataAugmented,
        key,
        strokeOnly,
        // strokeWidth: 10,
    }
}
